import {
  ShorexDrawerContent,
  ShorexDrawerContentProps
} from "@silversea-ssc/ui-components-react";
import React from "react";

import {
  SanityMicroContentExcursions,
  SanityShorexesSelectionConfirmedShorex
} from "../use-sanity-shorex-content";
import { shorexSplitNewFeature } from "../shorex-split-new-feature";

import ContentExcursionInfoBox from "./ContentExcursionInfoBox";
import { useLingui } from "@lingui/react/macro";
import { formatI18nCurrency } from "src/utils/localization/formatCurrency";
import { useClientSideFeatureFlag } from "src/utils/feature-flags";
import { isPresent } from "src/utils/checks";
import { formatShorexPrice } from "../format-shorex-price";

type Props = {
  cruiseType: ShorexDrawerContentProps["cruiseType"];
  hideDurationAndCode: boolean;
  shorex?: SanityShorexesSelectionConfirmedShorex;
  microContent: SanityMicroContentExcursions | undefined;
  isLoading: boolean;
};

const SingleShorexDrawerContent: React.FC<Props> = ({
  cruiseType,
  hideDurationAndCode,
  shorex: sanityShorex,
  microContent,
  isLoading
}) => {
  const { t } = useLingui();
  const { shorex, duration, price } = sanityShorex || {};
  const isUnbundling = useClientSideFeatureFlag("unbundling");

  if (isLoading) return "Loading...";
  if (!shorex || !microContent) return null;

  const {
    activityLevels = [],
    accessibilityLevels = [],
    features = [],
    subcategories = []
  } = microContent;

  const activityLevelContent = activityLevels.find(
    (l) => l?.iconStep === shorex.activityLevel
  );
  const accessibilityLevelContent = accessibilityLevels.find(
    (l) => l?.code === shorex.accessibility
  );
  const subcategoriesContent = subcategories.find(
    (l) => l?.code === shorex.subcategory
  );

  const { featureNew, otherFeatures } = shorexSplitNewFeature(
    shorex.features,
    features
  );
  const formattedPrice =
    isUnbundling && price ? formatShorexPrice(price) : undefined;

  return (
    <ShorexDrawerContent
      code={`${t`Code`}: ${shorex.code}`}
      cruiseType={cruiseType}
      excursionInfoBox={
        <ContentExcursionInfoBox
          accessibilityLevel={accessibilityLevelContent}
          activityLevel={activityLevelContent}
          features={otherFeatures.filter(isPresent)}
          hideDurationAndCode={hideDurationAndCode}
          duration={duration}
        />
      }
      image={shorex.image}
      title={shorex.name || ""}
      priceFormattedAmount={isUnbundling ? formattedPrice : undefined}
      newTagLabel={featureNew?.label}
      mainCategory={isUnbundling ? shorex.category : undefined}
      subCategory={isUnbundling ? subcategoriesContent?.label : undefined}
      note={
        shorex.noteHtml ? (
          <div
            dangerouslySetInnerHTML={{
              __html: shorex.noteHtml
            }}
          ></div>
        ) : undefined
      }
    >
      {shorex.descriptionHtml && (
        <div
          dangerouslySetInnerHTML={{
            __html: shorex.descriptionHtml
          }}
        ></div>
      )}
    </ShorexDrawerContent>
  );
};

export default SingleShorexDrawerContent;
